/* Defines which properties of which asset types to show */

import React from "react";
import { FormattedMessage } from "react-intl";

import { satisfiesRecord } from "@nens/shared-utils";

import { PropertyName } from "../types/assets";

export type EditType = "setValue" | "openclose";

export interface PropertyInformation {
  property: PropertyName;
  prettyName?: React.FC; // Otherwise the property is shown as is
  transformValue?: (value: string | number) => string | number;
  show: ("always" | "edit" | "hidden" | "never")[];
  edits?: EditType[]; // needed when show === "edit"
  unit?: string;
}

// An overview of existing property definitions, they are used below per asset type
// in the definition of ASSET_PROPERTIES.

// It might seem that there is duplication in the keys of the object below and the
// 'property' element, but this is not necessarily so. For instance the key is 'pumpType'
// even though the property on Pumps is actually called 'type', for clarity. And there
// can be that the details will change depending on asset type (e.g. some assets want the property
// to be always visible, some want it hidden), then both versions can be in this object
// under different keys.

// Initially all properties are of type "string", and will be just printed to the
// screen as-is. It is expected that many of them will get their own widgets (e.g.
// numbers like cross_section_shape that are realy Enums), they can be added in
// ShowPropertyInfo() in SelectedAssetInspector.

const PROP_DEFS = satisfiesRecord<PropertyInformation>()({
  id: {
    property: "id",
    prettyName: () => <FormattedMessage id="oneDElementInspector.id" defaultMessage="ID" />,
    show: ["hidden"],
  },
  dmax: {
    property: "dmax",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.dmax" defaultMessage="Bottom level" />
    ),
    show: ["hidden"],
  },
  content_pk: {
    property: "content_pk",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.contentPk" defaultMessage="Source table ID" />
    ),
    show: ["hidden"],
  },
  bottom_level: {
    property: "bottom_level",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.bottomLevel" defaultMessage="Bottom Level" />
    ),
    show: ["hidden"],
    unit: "m MSL",
  },
  calculation_type: {
    // XXX: Make enum
    property: "calculation_type",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.calculationType"
        defaultMessage="Calculation Type"
      />
    ),
    show: ["hidden"],
  },
  capacity: {
    property: "capacity",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.capacity" defaultMessage="Capacity" />
    ),
    show: ["hidden", "edit"],
    edits: ["openclose", "setValue"],
    unit: "m³/s",
  },
  code: {
    property: "code",
    prettyName: () => <FormattedMessage id="oneDElementInspector.code" defaultMessage="Code" />,
    show: ["hidden"],
  },
  connection_node_end: {
    property: "connection_node_end",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.connectionNodeEnd"
        defaultMessage="Connection node end ID"
      />
    ),
    show: ["hidden"],
  },
  connection_node_start: {
    property: "connection_node_start",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.connectionNodeStart"
        defaultMessage="Connection node start ID"
      />
    ),
    show: ["hidden"],
  },
  crest_level: {
    property: "crest_level",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.crestLevel" defaultMessage="Crest level" />
    ),
    show: ["hidden", "edit"],
    edits: ["setValue"],
    unit: "m MSL",
  },
  crest_type: {
    property: "crest_type",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.crestType" defaultMessage="Crest Type" />
    ),
    show: ["hidden"],
  },
  gate_level: {
    property: "gate_level",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.setGateOpening"
        defaultMessage="Set gate opening"
      />
    ),
    show: ["edit"],
    edits: ["setValue"],
    unit: "m",
  },
  cross_section_table: {
    property: "cross_section_table",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.crossSectionTableData"
        defaultMessage="Cross section table data"
      />
    ),
    show: ["hidden"],
    unit: "m",
  },
  cross_section_height: {
    property: "cross_section_height",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.crossSectionHeight"
        defaultMessage="Cross section height"
      />
    ),
    show: ["hidden"],
    unit: "m",
  },
  cross_section_shape: {
    property: "cross_section_shape",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.crossSectionShape"
        defaultMessage="Cross section shape"
      />
    ),
    show: ["hidden"],
  },
  cross_section_width: {
    property: "cross_section_width",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.crossSectionWidth"
        defaultMessage="Cross section width"
      />
    ),
    show: ["hidden"],
    unit: "m",
  },
  discharge_coefficient: {
    property: "discharge_coefficient",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.dischargeCoefficient"
        defaultMessage="Discharge coefficient"
      />
    ),
    show: ["hidden", "edit"],
    edits: ["openclose"],
  },
  discharge_coefficient_negative: {
    property: "discharge_coefficient_negative",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.dischargeCoefficientNegative"
        defaultMessage="Discharge coefficient neg"
      />
    ),
    show: ["hidden"],
  },
  discharge_coefficient_positive: {
    property: "discharge_coefficient_positive",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.dischargeCoefficientPositive"
        defaultMessage="Discharge coefficient pos"
      />
    ),
    show: ["hidden", "edit"],
    edits: ["openclose"],
  },
  display_name: {
    property: "display_name",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.displayName" defaultMessage="Display Name" />
    ),
    show: ["always"],
  },
  dist_calc_points: {
    property: "dist_calc_points",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.distCalcPoints"
        defaultMessage="Dist calc points"
      />
    ),
    show: ["hidden"],
  },
  drain_level: {
    property: "drain_level",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.drainLevel" defaultMessage="Drain Level" />
    ),
    show: ["hidden"],
    unit: "m MSL",
  },
  friction_type: {
    property: "friction_type",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.frictionType" defaultMessage="Friction type" />
    ),
    show: ["hidden"],
  },
  friction_value: {
    property: "friction_value",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.frictionValue" defaultMessage="Friction" />
    ),
    show: ["hidden"],
  },
  initial_waterlevel: {
    property: "initial_waterlevel",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.initialWaterlevel"
        defaultMessage="Initial waterlevel"
      />
    ),
    show: ["hidden"],
    unit: "m MSL",
  },
  invert_level_end_point: {
    property: "invert_level_end_point",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.invertLevelEndPoint"
        defaultMessage="Invert Level End Point"
      />
    ),
    show: ["hidden"],
    unit: "m MSL",
  },
  invert_level_start_point: {
    property: "invert_level_start_point",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.invertLevelStartPoint"
        defaultMessage="Invert Level Start Point"
      />
    ),
    show: ["hidden"],
    unit: "m MSL",
  },
  kcu: {
    // XXX What is this
    property: "kcu",
    prettyName: () => <FormattedMessage id="oneDElementInspector.kcu" defaultMessage="Kcu" />,
    show: ["never"],
  },
  levbr: {
    property: "levbr",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.levbr" defaultMessage="Maximum depth" />
    ),
    show: ["hidden"],
  },
  levl: {
    property: "levl",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.levl" defaultMessage="Exchange level" />
    ),
    // Before this property contained the id of the 1D flow line from which the exchange level could be read.
    // So whole numbers a.k.a id's should return N/A
    transformValue: (value) => (Number.isInteger(+value) ? "N/A" : value),
    show: ["hidden"],
  },
  levmat: {
    property: "levmat",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.levmat" defaultMessage="Material" />
    ),
    show: ["hidden"],
  },
  line_id: {
    property: "id",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.lineID" defaultMessage="Line ID" />
    ),
    show: ["hidden"],
  },
  lower_stop_level: {
    property: "lower_stop_level",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.lowerStopLevel"
        defaultMessage="Lower stop level"
      />
    ),
    show: ["hidden"],
    unit: "m MSL",
  },
  material: {
    property: "material",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.material" defaultMessage="Material" />
    ),
    show: ["hidden"],
  },
  max_breach_depth: {
    property: "max_breach_depth",
    prettyName: () => (
      <FormattedMessage
        id="oneDElementInspector.maxBreachDepth"
        defaultMessage="Max breach depth"
      />
    ),
    show: ["hidden"],
    unit: "m",
  },
  node_id: {
    property: "id",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.nodeID" defaultMessage="Node ID" />
    ),
    show: ["hidden"],
  },
  node_type: {
    property: "node_type",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.nodeType" defaultMessage="Node type" />
    ),
    show: ["always"],
  },
  pumpType: {
    property: "type",
    prettyName: () => <FormattedMessage id="oneDElementInspector.pumpType" defaultMessage="Type" />,
    show: ["hidden"],
  },
  sewerage: {
    property: "sewerage",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.sewerage" defaultMessage="Sewerage" />
    ),
    show: ["hidden"],
  },
  sewerage_type: {
    property: "sewerage_type",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.sewerageType" defaultMessage="Sewerage Type" />
    ),
    show: ["hidden"],
  },
  shape: {
    property: "shape",
    prettyName: () => <FormattedMessage id="oneDElementInspector.shape" defaultMessage="Shape" />,
    show: ["hidden"],
  },
  start_level: {
    property: "start_level",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.startLevel" defaultMessage="Start Level" />
    ),
    show: ["hidden"],
    unit: "m MSL",
  },
  storage_area: {
    property: "storage_area",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.storage_area" defaultMessage="Storage area" />
    ),
    show: ["hidden"],
    unit: "m²",
  },
  sumax: {
    property: "sumax",
    prettyName: () => <FormattedMessage id="oneDElementInspector.sumax" defaultMessage="Sumax" />,
    show: ["hidden"],
  },
  surface_level: {
    property: "surface_level",
    prettyName: () => (
      <FormattedMessage id="oneDElementInspector.surfaceLevel" defaultMessage="Surface Level" />
    ),
    show: ["hidden"],
  },
  width: {
    property: "width",
    prettyName: () => <FormattedMessage id="oneDElementInspector.width" defaultMessage="Width" />,
    show: ["hidden"],
    unit: "m",
  },
});

export const ASSET_PROPERTIES = satisfiesRecord<PropertyInformation[]>()({
  // Note order is alphabetic; within an object type the properties
  // are in the order they will be displayed in.
  // Compare with asset type definitions in types/assets.ts.
  breaches: [
    PROP_DEFS.content_pk,
    PROP_DEFS.code,
    PROP_DEFS.display_name,
    PROP_DEFS.kcu,
    // comment out because we already show discharge_coefficient_positive and discharge_coefficient_negative
    // PROP_DEFS.discharge_coefficient,
    PROP_DEFS.discharge_coefficient_positive,
    // instead of re-using the PROP_DEFS.discharge_coefficient_negative we redefine it here, because it needs to work different for breaches as for other assets (like for example the asset weir)
    // previously we did re-use PROP_DEFS.discharge_coefficient_negative and it led to this bug that status was shown double for weirs
    // https://github.com/nens/threedi-live-site/issues/1044
    // Is redefining here a good solution? Maybe not, but in that case we might want to refactor a lot.
    {
      property: "discharge_coefficient_negative",
      prettyName: () => (
        <FormattedMessage
          id="oneDElementInspector.dischargeCoefficientNegativeForBreach"
          defaultMessage="Discharge coefficient neg"
        />
      ),
      show: ["hidden", "edit"],
      edits: ["openclose"],
    },
    PROP_DEFS.levmat,
    PROP_DEFS.levbr,
    PROP_DEFS.levl,
  ],
  channels: [
    PROP_DEFS.content_pk,
    PROP_DEFS.code,
    PROP_DEFS.kcu,
    PROP_DEFS.dist_calc_points,
    PROP_DEFS.calculation_type,
    PROP_DEFS.discharge_coefficient,
  ],
  culverts: [
    PROP_DEFS.content_pk,
    PROP_DEFS.display_name,
    PROP_DEFS.code,
    PROP_DEFS.kcu,
    PROP_DEFS.calculation_type,
    PROP_DEFS.cross_section_shape,
    PROP_DEFS.cross_section_width,
    PROP_DEFS.cross_section_height,
    PROP_DEFS.cross_section_table,
    PROP_DEFS.friction_value,
    PROP_DEFS.friction_type,
    PROP_DEFS.dist_calc_points,
    PROP_DEFS.discharge_coefficient_positive,
    PROP_DEFS.discharge_coefficient_negative,
    PROP_DEFS.invert_level_start_point,
    PROP_DEFS.invert_level_end_point,
    PROP_DEFS.calculation_type,
  ],
  levees: [PROP_DEFS.content_pk, PROP_DEFS.crest_level, PROP_DEFS.max_breach_depth],
  manholes: [
    PROP_DEFS.content_pk,
    PROP_DEFS.display_name,
    PROP_DEFS.calculation_type,
    PROP_DEFS.sumax,
    PROP_DEFS.bottom_level,
    PROP_DEFS.surface_level,
    PROP_DEFS.drain_level,
    PROP_DEFS.width,
    PROP_DEFS.shape,
    PROP_DEFS.storage_area,
    PROP_DEFS.initial_waterlevel,
  ],
  nodes: [
    PROP_DEFS.bottom_level,
    PROP_DEFS.calculation_type,
    PROP_DEFS.dmax,
    PROP_DEFS.id,
    {
      property: "content_pk",
      prettyName: () => (
        <FormattedMessage
          id="oneDElementInspector.contentPkNode"
          defaultMessage="Connection node ID"
        />
      ),
      show: ["hidden"],
    },
    PROP_DEFS.node_type,
    PROP_DEFS.sumax,
  ],
  orifices: [
    PROP_DEFS.content_pk,
    PROP_DEFS.display_name,
    PROP_DEFS.code,
    PROP_DEFS.kcu,
    PROP_DEFS.friction_value,
    PROP_DEFS.friction_type,
    PROP_DEFS.discharge_coefficient_positive,
    PROP_DEFS.discharge_coefficient_negative,
    PROP_DEFS.crest_level,
    PROP_DEFS.crest_type,
    // AWAITING BACKEND ENABLE AGAIN ONCE BACKEND HAS BEEN COMPLETED
    // PROP_DEFS.gate_level,
    PROP_DEFS.sewerage,
  ],
  pipes: [
    PROP_DEFS.content_pk,
    PROP_DEFS.display_name,
    PROP_DEFS.material,
    PROP_DEFS.kcu,
    PROP_DEFS.friction_value,
    PROP_DEFS.sewerage_type,
    PROP_DEFS.cross_section_shape,
    PROP_DEFS.cross_section_width,
    PROP_DEFS.cross_section_height,
    PROP_DEFS.cross_section_table,
    PROP_DEFS.invert_level_start_point,
    PROP_DEFS.invert_level_end_point,
    PROP_DEFS.calculation_type,
    PROP_DEFS.discharge_coefficient,
  ],
  pumps: [
    PROP_DEFS.content_pk,
    PROP_DEFS.display_name,
    PROP_DEFS.start_level,
    PROP_DEFS.lower_stop_level,
    PROP_DEFS.capacity,
    PROP_DEFS.pumpType,
  ],
  weirs: [
    PROP_DEFS.content_pk,
    PROP_DEFS.display_name,
    PROP_DEFS.code,
    PROP_DEFS.kcu,
    PROP_DEFS.cross_section_shape,
    PROP_DEFS.cross_section_width,
    PROP_DEFS.cross_section_height,
    PROP_DEFS.cross_section_table,
    PROP_DEFS.crest_level,
    PROP_DEFS.crest_type,
    PROP_DEFS.discharge_coefficient_positive,
    PROP_DEFS.discharge_coefficient_negative,
    PROP_DEFS.friction_value,
    PROP_DEFS.friction_type,
    PROP_DEFS.sewerage,
  ],
});
